import { useCallback, useEffect, useState } from "react";
import api, { API_BASE, formatError } from "@/lib/api";
import { SeverityBadge } from "@/lib/severity";
import { useFilters } from "@/context/FilterContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, Trash2, Pencil, Download, ChevronLeft, ChevronRight, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";
import InputManualDialog from "@/components/dialogs/InputManualDialog";

export default function EventExplorer() {
  const { params, meta } = useFilters();
  const { isAdmin } = useAuth();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize] = useState(25);
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("tanggal");
  const [order, setOrder] = useState("desc");
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/failures", {
        params: { ...params, q: q || undefined, skip: page * pageSize, limit: pageSize, sort, order },
      });
      setRows(data.items);
      setTotal(data.total);
    } finally { setLoading(false); }
  }, [params, q, page, pageSize, sort, order]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const del = async (id) => {
    if (!window.confirm("Delete this failure event?")) return;
    try {
      await api.delete(`/failures/${id}`);
      toast.success("Event deleted");
      fetchRows();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || "Delete failed");
    }
  };

  const exportXlsx = () => {
    const token = localStorage.getItem("trams_token");
    const qs = new URLSearchParams(params).toString();
    fetch(`${API_BASE}/exports/failures?${qs}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => { if (!r.ok) throw new Error(); return r.blob(); })
      .then(blob => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a"); a.href = url;
        a.download = `Gangguan_Operasi_${params.start}_to_${params.end}.xlsx`; a.click();
        URL.revokeObjectURL(url);
        toast.success("Excel export downloaded");
      })
      .catch(() => toast.error("Export failed"));
  };

  const exportCsv = () => {
    const headers = ["Tanggal","Waktu","No TS","Equipment","Kejadian","Penyebab","Lokasi","Delay (Min)","Dampak","Status","Klasifikasi PIC"];
    const lines = [headers.join(",")];
    rows.forEach(r => {
      const row = [
        r.tanggal, r.waktu, r.no_ts, r.equipment, r.kejadian, r.penyebab, r.lokasi,
        r.delay_seconds ? (r.delay_seconds/60).toFixed(1) : 0,
        r.dampak, r.status, r.klasifikasi_pic
      ].map(v => `"${(v||"").toString().replace(/"/g,'""')}"`).join(",");
      lines.push(row);
    });
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "trams_events.csv"; a.click(); URL.revokeObjectURL(url);
  };

  const toggleSort = (key) => {
    if (sort === key) setOrder(order === "asc" ? "desc" : "asc");
    else { setSort(key); setOrder("desc"); }
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="space-y-4 pb-8" data-testid="event-explorer">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          ["Total Events", total.toLocaleString(), "text-[#1e40af]"],
          ["Currently Filtered", rows.length + (page > 0 ? ` (page ${page+1})` : ""), "text-slate-900"],
          ["Search", q ? `"${q}"` : "—", "text-[#f59e0b]"],
          ["Sort", `${sort} ${order === "asc" ? "↑" : "↓"}`, "text-[#22c55e]"],
        ].map(([label, v, cls]) => (
          <div key={label} className="bg-white p-4 rounded-xl border border-slate-200/60 kpi-shadow">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">{label}</div>
            <div className={`text-base font-black mt-1 ${cls} truncate`}>{v}</div>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-slate-200/60 kpi-shadow p-6">
        <div className="flex items-center justify-between mb-4 gap-4 flex-wrap">
          <div className="flex-1 max-w-md relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Search kejadian, penyebab, no. TS, lokasi…"
              value={q}
              onChange={(e) => { setPage(0); setQ(e.target.value); }}
              className="pl-10 h-10"
              data-testid="events-search"
            />
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={exportCsv} data-testid="events-export-csv">
              <Download className="w-3.5 h-3.5 mr-1.5" /> Export CSV
            </Button>
            <Button className="bg-[#1e40af] hover:bg-[#1e3a8a]" onClick={exportXlsx} data-testid="events-export-xlsx">
              <FileSpreadsheet className="w-3.5 h-3.5 mr-1.5" /> Export XLSX
            </Button>
          </div>
        </div>

        <div className="overflow-x-auto trams-scroll">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-200">
                {[
                  ["tanggal","Date"],["waktu","Time"],["no_ts","Train"],
                  ["nomor_id_operasi","ID Operasi"],
                  ["equipment","Subsystem"],
                  ["penyebab","Cause"],["lokasi","Location"],
                  ["delay_seconds","Delay"],["dampak","Impact"],["status","Status"]
                ].map(([k,label]) => (
                  <th key={k} className="text-left font-semibold pb-3 pr-3 cursor-pointer hover:text-slate-900" onClick={() => toggleSort(k)}>
                    {label}{sort === k ? (order === "asc" ? " ▲" : " ▼") : ""}
                  </th>
                ))}
                {isAdmin && <th className="text-right font-semibold pb-3">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={11} className="text-center py-8 text-slate-400 text-xs">Loading…</td></tr>}
              {!loading && rows.length === 0 && <tr><td colSpan={11} className="text-center py-8 text-slate-400 text-xs">No events found.</td></tr>}
              {!loading && rows.map(r => (
                <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50/50">
                  <td className="py-2.5 pr-3">{r.tanggal}</td>
                  <td className="py-2.5 pr-3">{r.waktu || "—"}</td>
                  <td className="py-2.5 pr-3">{r.no_ts || "—"}</td>
                  <td className="py-2.5 pr-3 font-mono text-xs" data-testid={`id-op-${r.id}`}>{r.nomor_id_operasi || "—"}</td>
                  <td className="py-2.5 pr-3">{r.equipment || "—"}</td>
                  <td className="py-2.5 pr-3 max-w-[220px] truncate" title={r.penyebab}>{r.penyebab || "—"}</td>
                  <td className="py-2.5 pr-3">{r.lokasi || "—"}</td>
                  <td className="py-2.5 pr-3 tabular-nums">{r.delay_seconds ? (r.delay_seconds/60).toFixed(1) : 0}</td>
                  <td className="py-2.5 pr-3 text-xs"><SeverityBadge value={r.dampak} /></td>
                  <td className="py-2.5 pr-3">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider border ${
                      r.status === "Closed" ? "bg-green-100 text-green-700 border-green-200" :
                      r.status === "Close Monitoring" ? "bg-amber-100 text-amber-700 border-amber-200" :
                      "bg-orange-100 text-orange-700 border-orange-200"
                    }`}>{r.status}</span>
                  </td>
                  {isAdmin && (
                    <td className="py-2.5 text-right whitespace-nowrap">
                      <button className="text-slate-500 hover:text-[#1e40af] p-1.5" onClick={() => setEditing(r)} data-testid={`edit-${r.id}`}>
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button className="text-slate-500 hover:text-red-600 p-1.5" onClick={() => del(r.id)} data-testid={`delete-${r.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between mt-4 text-xs text-slate-500">
          <div>Showing {rows.length ? page * pageSize + 1 : 0}–{Math.min((page + 1) * pageSize, total)} of {total.toLocaleString()} events</div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(p => p - 1)} data-testid="events-prev">
              <ChevronLeft className="w-3.5 h-3.5" />
            </Button>
            <div>Page <span className="font-semibold">{page + 1}</span> / {totalPages}</div>
            <Button variant="outline" size="sm" disabled={page + 1 >= totalPages} onClick={() => setPage(p => p + 1)} data-testid="events-next">
              <ChevronRight className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>
      </div>

      <InputManualDialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)} initial={editing} onSaved={fetchRows} />
    </div>
  );
}
